import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Lock, Mail, ArrowLeft } from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_telemedicine-plus-1/artifacts/si76u1vy_image.png";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email, password);
      toast.success("Welcome back, admin!");
      navigate("/admin");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Login failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[color:var(--bg-cream)]">
      <div className="hidden lg:flex w-1/2 brand-gradient relative items-center justify-center p-12">
        <div className="absolute inset-0 grain opacity-30" />
        <div className="relative text-white max-w-md">
          <img src={LOGO} alt="HappiBandhu" className="h-20 w-20 bg-white/10 rounded-2xl p-2 mb-8" />
          <h1 className="font-display font-bold text-4xl tracking-tight leading-tight">
            HappiBandhu<br />Admin Console
          </h1>
          <p className="mt-5 text-white/85 leading-relaxed">
            Track visitors, monitor product engagement, and oversee the wellness
            membership platform — all from one secure dashboard.
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 sm:p-12 relative">
        <Link
          to="/"
          data-testid="back-home-link"
          className="absolute top-6 left-6 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[color:var(--brand-navy)]"
        >
          <ArrowLeft className="h-4 w-4" /> Back to site
        </Link>
        <form
          onSubmit={submit}
          data-testid="admin-login-form"
          className="w-full max-w-md bg-white rounded-3xl shadow-xl border border-slate-100 p-8 sm:p-10"
        >
          <div className="lg:hidden mb-6 flex items-center gap-3">
            <img src={LOGO} alt="HappiBandhu" className="h-12 w-12" />
            <div className="font-display font-bold text-xl text-[color:var(--brand-navy)]">
              HappiBandhu
            </div>
          </div>
          <h2 className="font-display font-bold text-2xl sm:text-3xl text-[color:var(--brand-navy)]">
            Admin Sign In
          </h2>
          <p className="text-slate-500 text-sm mt-2 mb-7">
            Enter your credentials to access the dashboard.
          </p>

          <div className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-slate-700">Email</Label>
              <div className="relative mt-1.5">
                <Mail className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  required
                  data-testid="admin-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-11"
                  autoComplete="email"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-700">Password</Label>
              <div className="relative mt-1.5">
                <Lock className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  required
                  data-testid="admin-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 h-11"
                  autoComplete="current-password"
                />
              </div>
            </div>

            {error && (
              <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
                {error}
              </div>
            )}

            <Button
              type="submit"
              data-testid="admin-login-submit"
              disabled={loading}
              className="w-full h-11 bg-[color:var(--brand-magenta)] hover:bg-[color:var(--brand-magenta-deep)] text-white font-semibold rounded-full"
            >
              {loading ? "Signing in..." : "Sign in"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
